import React, { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import MarketingLayout from '../components/MarketingLayout';

const FEATURES = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Reporter Database',
    desc: 'Access thousands of journalists with beat tracking, contact info, and coverage history.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Outreach Engine',
    desc: 'Send personalized pitches at scale with staggered delivery and smart scheduling.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    title: 'PR CRM',
    desc: 'Manage contacts, track conversations, and never lose the thread with a reporter.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    title: 'Coverage Tracking',
    desc: 'Log hits, track coverage, and prove ROI to clients with built-in reporting.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: 'AI-Powered Pitches',
    desc: 'Let AI draft personalized pitches, analyze beat relevance, and suggest the best angle.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Analytics & Reports',
    desc: 'Understand reply rates, track team performance, and report on what\'s working.',
  },
];

const STATS = [
  { value: '50,000+', label: 'Reporters tracked', note: 'Refreshed continuously via RSS + enrichment' },
  { value: '10,000+', label: 'Publications indexed', note: 'From national press to niche trades' },
  { value: '98%', label: 'Deliverability', note: 'Verified emails + sending warm-up' },
  { value: '3×', label: 'Avg. reply-rate lift', note: 'Reported by teams who personalize at scale' },
];

// Publications PitchFriendly has been used to land coverage in.
const PUBLICATIONS = [
  'The New York Times',
  'The Wall Street Journal',
  'Forbes',
  'Bloomberg',
  'TechCrunch',
  'Fortune',
  'Business Insider',
  'Axios',
  'Inc.',
  'VentureBeat',
];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    document.title = 'PitchFriendly — PR Software for Modern Agencies';
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement('meta');
      desc.setAttribute('name', 'description');
      document.head.appendChild(desc);
    }
    desc.setAttribute('content', 'PitchFriendly is PR software that helps agencies manage reporter relationships, run targeted outreach, track coverage, and prove ROI. Try free.');

    let og = document.querySelector('meta[property="og:title"]');
    if (!og) {
      og = document.createElement('meta');
      og.setAttribute('property', 'og:title');
      document.head.appendChild(og);
    }
    og.setAttribute('content', 'PitchFriendly — PR Software for Modern Agencies');
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-paper">
        {/* restrained warm glow — cream stays dominant */}
        <div className="absolute inset-x-0 top-0 h-[640px] pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-14%] left-1/2 -translate-x-1/2 w-[820px] h-[460px] bg-berry-300/15 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-berry-50 border border-berry-100 text-berry-700 text-xs font-medium mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-berry-500 animate-pulse" aria-hidden="true" />
            Built for PR teams that mean business
          </div>
          <h1 className="font-serif font-medium tracking-[-0.02em] leading-[0.99] mb-6 text-ink-900 text-[clamp(2.6rem,6vw,5rem)]">
            PR software you&rsquo;ll<br />
            <span className="italic text-berry-500">actually <span className="mark-underline">enjoy</span> using.</span>
          </h1>
          <p className="text-lg sm:text-xl text-ink-600 max-w-2xl mx-auto mb-9 leading-relaxed">
            A reporter database, outreach engine, and coverage tracker in one place — without the enterprise bloat or the five-figure contract. Warm, fast, and built for the way PR actually works.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-3">
            <Link
              to="/register"
              className="w-full sm:w-auto px-8 py-3.5 bg-berry-500 hover:bg-berry-600 text-white font-semibold rounded-lg text-base shadow-sm hover:shadow-md transition-all duration-150 ease-editorial hover:-translate-y-px active:translate-y-0"
            >
              Start free — no credit card
            </Link>
            <a
              href="#how-it-works"
              className="group inline-flex items-center gap-1.5 text-base font-semibold text-ink-700 hover:text-berry-600 transition-colors duration-150"
            >
              See how it works
              <span className="transition-transform duration-150 ease-editorial group-hover:translate-x-0.5" aria-hidden="true">→</span>
            </a>
          </div>
          <p className="mt-5 text-xs text-ink-500">Free plan includes 100 contacts · No setup fees · Cancel anytime</p>

          {/* Product preview — editorial frame, staged on a faint warm bloom */}
          <div className="relative mt-16 max-w-4xl mx-auto">
            <div className="absolute -inset-x-10 -top-8 bottom-2 bg-gradient-to-b from-berry-200/25 via-butter-100/20 to-transparent blur-2xl rounded-[40px] pointer-events-none" aria-hidden="true" />
            <div
              className="relative rounded-2xl border border-ink-200 bg-surface shadow-lg overflow-hidden text-left"
              role="img"
              aria-label="PitchFriendly app preview: a reporters view with reply-rate stats and outreach status."
            >
              <div className="flex items-center gap-2 px-4 py-2.5 bg-ink-50 border-b border-ink-100">
                <span className="w-2.5 h-2.5 rounded-full bg-berry-400" aria-hidden="true" />
                <span className="ml-1.5 text-xs font-mono text-ink-500">app.pitchfriendly.com/reporters</span>
              </div>
              <div className="p-6 grid grid-cols-3 gap-4" aria-hidden="true">
                {[
                  { label: 'Total Reporters', value: '12,483' },
                  { label: 'Messages Sent', value: '3,241' },
                  { label: 'Reply Rate', value: '28%' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-paper rounded-xl border border-ink-100 p-4">
                    <p className="text-[11px] text-ink-500 uppercase tracking-wide mb-1">{stat.label}</p>
                    <p className="font-serif text-2xl font-semibold text-ink-900 tabular-nums">{stat.value}</p>
                    <p className="text-xs text-success-600 mt-1">↑ vs last month</p>
                  </div>
                ))}
              </div>
              <div className="px-6 pb-6" aria-hidden="true">
                <div className="bg-paper rounded-xl border border-ink-100 divide-y divide-ink-100">
                  <div className="flex items-center justify-between px-4 py-2.5 text-[11px] uppercase tracking-wide text-ink-400">
                    <span>Reporter</span>
                    <span>Status</span>
                  </div>
                  {[
                    { name: 'Sarah Chen', beat: 'Tech & AI', pub: 'TechCrunch', status: 'Replied' },
                    { name: 'Marcus Williams', beat: 'Finance', pub: 'Bloomberg', status: 'Opened' },
                    { name: 'Priya Patel', beat: 'Healthcare', pub: 'STAT News', status: 'Sent' },
                    { name: 'Daniel Okafor', beat: 'Climate', pub: 'The Guardian', status: 'Sent' },
                  ].map((row) => (
                    <div key={row.name} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm text-ink-900 font-medium">{row.name}</p>
                        <p className="text-xs text-ink-500">{row.beat} · {row.pub}</p>
                      </div>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                        row.status === 'Replied' ? 'bg-success-50 text-success-700' :
                        row.status === 'Opened' ? 'bg-berry-50 text-berry-700' :
                        'bg-ink-100 text-ink-600'
                      }`}>{row.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Publication proof wall — coverage landed via PitchFriendly */}
      <section className="bg-paper relative z-10 pt-4 pb-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold text-ink-500 uppercase tracking-[0.18em] mb-7">
            Used to land coverage in
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3.5">
            {PUBLICATIONS.map((pub) => (
              <li key={pub} className="font-serif text-lg sm:text-xl text-ink-500">
                {pub}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-surface border-y border-ink-100 py-12 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-serif text-4xl sm:text-5xl font-semibold text-ink-900 tabular-nums">{s.value}</p>
                <p className="text-sm font-medium text-ink-700 mt-1.5">{s.label}</p>
                <p className="text-xs text-ink-500 mt-1 max-w-[18ch] mx-auto leading-snug">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="how-it-works" className="py-24 bg-paper relative z-10 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl sm:text-5xl font-medium text-ink-900 tracking-tight">Everything you need to run PR</h2>
            <p className="text-ink-600 mt-4 max-w-xl mx-auto text-lg">From finding the right reporter to logging the win — PitchFriendly handles the full workflow.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="group bg-surface rounded-2xl border border-ink-100 p-6 shadow-xs hover:shadow-md hover:border-ink-200 hover:-translate-y-0.5 transition-all duration-200 ease-editorial"
              >
                <div className="w-11 h-11 rounded-xl bg-berry-50 text-berry-600 flex items-center justify-center mb-4 transition-colors duration-200 group-hover:bg-berry-100">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-ink-900 mb-2 text-lg">{f.title}</h3>
                <p className="text-sm text-ink-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/features" className="group inline-flex items-center gap-1.5 text-berry-700 font-medium hover:text-berry-800 text-sm">
              Explore all features
              <span className="transition-transform duration-150 ease-editorial group-hover:translate-x-0.5" aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Founder note (honest, on-brand human voice) */}
      <section className="py-24 bg-surface border-t border-ink-100 relative z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold text-ink-500 uppercase tracking-widest mb-6">Why we built PitchFriendly</p>
          <blockquote className="font-serif text-2xl sm:text-[2rem] leading-[1.35] text-ink-800">
            PR software is cold, clunky, and priced for the Fortune 500. We’ve worked PR from every side — in-house, agency, and client — and built the tool we always wished we had: one that feels like a good newsroom,
            {' '}<span className="italic text-berry-600">warm, fast, and on your side.</span>
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3.5">
            <div className="flex -space-x-2" aria-hidden="true">
              <span className="w-10 h-10 rounded-full bg-berry-100 text-berry-700 ring-2 ring-surface flex items-center justify-center text-sm font-semibold">JA</span>
              <span className="w-10 h-10 rounded-full bg-pine-100 text-pine-700 ring-2 ring-surface flex items-center justify-center text-sm font-semibold">PD</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-ink-900">Joel Andren &amp; Paul Denya</p>
              <p className="text-xs text-ink-500">Co-founders, PitchFriendly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="py-24 bg-paper border-t border-ink-100 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-5xl font-medium text-ink-900 tracking-tight">Simple, transparent pricing</h2>
            <p className="text-ink-600 mt-4 text-lg">No demos. No annual contracts. No five-figure quotes — start in two minutes.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto items-stretch">
            {[
              { name: 'Free', price: '$0', desc: 'For solo PR pros', highlight: false, perks: ['100 contacts', 'Reporter database access', 'Send from your inbox'] },
              { name: 'Pro', price: '$40', unit: '/mo', desc: 'For growing agencies', highlight: true, perks: ['Unlimited contacts', 'AI-drafted pitches', 'Coverage reports & ROI'] },
              { name: 'Team', price: '$30', unit: '/seat', desc: 'For collaborative teams', highlight: false, perks: ['Shared workspace & roles', 'Client reporting', 'Priority support'] },
            ].map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border p-6 flex flex-col ${
                  plan.highlight
                    ? 'bg-surface border-berry-300 ring-1 ring-berry-200 shadow-md'
                    : 'bg-surface border-ink-200 shadow-xs'
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-berry-500 text-white text-[11px] font-semibold uppercase tracking-wide shadow-sm">
                    Most popular
                  </span>
                )}
                <h3 className="text-base font-semibold text-ink-900 mb-1">{plan.name}</h3>
                <p className="mb-1">
                  <span className={`font-serif text-4xl font-semibold ${plan.highlight ? 'text-berry-600' : 'text-ink-900'}`}>{plan.price}</span>
                  {plan.unit && <span className="text-sm text-ink-500 font-medium">{plan.unit}</span>}
                </p>
                <p className="text-sm text-ink-600 mb-5">{plan.desc}</p>
                <ul className="space-y-2.5 mb-6 text-left">
                  {plan.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm text-ink-700">
                      <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-berry-500' : 'text-success-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {perk}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/pricing"
                  className={`mt-auto block w-full py-2.5 px-4 rounded-lg text-sm font-medium text-center transition-all duration-150 ease-editorial ${
                    plan.highlight
                      ? 'bg-berry-500 text-white hover:bg-berry-600 shadow-sm hover:shadow-md hover:-translate-y-px'
                      : 'bg-ink-100 text-ink-900 hover:bg-ink-200'
                  }`}
                >
                  {plan.highlight ? 'Start 14-day Pro trial' : 'See details'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA — the single solid-berry crescendo */}
      <section className="relative overflow-hidden bg-berry-700 text-white text-center py-28 z-10">
        <span className="pointer-events-none select-none absolute inset-0 flex items-center justify-center font-serif text-[26vw] font-semibold text-white/[0.04] leading-none" aria-hidden="true">
          Covered.
        </span>
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif text-4xl sm:text-5xl font-medium mb-4 tracking-tight">Ready to pitch smarter?</h2>
          <p className="text-berry-100 mb-8 text-lg leading-relaxed">Join PR teams using PitchFriendly to find better contacts, send better pitches, and land more coverage.</p>
          <Link
            to="/register"
            className="focus-on-dark inline-block px-10 py-4 bg-white text-berry-700 font-semibold rounded-lg hover:bg-berry-50 text-base shadow-lg hover:shadow-xl transition-all duration-150 ease-editorial hover:-translate-y-px active:translate-y-0"
          >
            Start free — no credit card
          </Link>
          <p className="mt-4 text-xs text-berry-100/90">No credit card required · Free plan available · Cancel anytime</p>
        </div>
      </section>
    </MarketingLayout>
  );
}
